import { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import type { KpiGroup, KpiMetric, MetricProvenance } from "../campaignTypes";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

const PROVENANCE_LABEL: Record<MetricProvenance, string | null> = {
  measured: null,
  calculated: "Calculated",
  modeled: "Modeled",
  unavailable: "Unavailable",
};

export default function PerformanceKpis({
  eyebrow,
  title,
  description,
  groups,
}: {
  eyebrow: string;
  title: string;
  description: string;
  groups: KpiGroup[];
}) {
  const visible = groups.filter((g) => g.metrics.length > 0);
  if (!visible.length) return null;

  return (
    <Box
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 6, md: 10 },
      }}
    >
      <Typography sx={eyebrowSx}>{eyebrow}</Typography>
      <Typography sx={sectionTitleSx}>{title}</Typography>
      <Typography sx={{ ...bodySx, maxWidth: 720, mb: 4 }}>{description}</Typography>
      {visible.map((group) => (
        <Box key={group.id} sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: portalFont,
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--portal-accent)",
              mb: 2,
            }}
          >
            {group.heading}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(3, 1fr)",
              },
              gap: 1.5,
            }}
          >
            {group.metrics.map((metric) => (
              <MetricCard key={metric.id} metric={metric} />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function MetricCard({ metric }: { metric: KpiMetric }) {
  const [open, setOpen] = useState(false);
  const provenance =
    metric.provenanceLabel ?? PROVENANCE_LABEL[metric.provenance];

  return (
    <Box sx={{ ...cardSurfaceSx, borderRadius: "24px", p: 2.5, minHeight: 150 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 2 }}>
        <Typography
          sx={{
            fontFamily: portalFont,
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--portal-text-muted)",
          }}
        >
          {metric.label}
        </Typography>
        {metric.explanation ? (
          <Tooltip
            title={metric.explanation}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            describeChild
          >
            <IconButton
              size="small"
              aria-label={`About ${metric.label}`}
              onClick={() => setOpen((v) => !v)}
              sx={{ color: "var(--portal-text-muted)", p: 0.25 }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontFamily: portalFont,
          fontWeight: 700,
          color: "var(--portal-text)",
          fontSize: "clamp(1.5rem, 1.2vw + 1rem, 2rem)",
          letterSpacing: "-0.02em",
          mb: 1.5,
        }}
      >
        {metric.value}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
        {metric.vsGoalLabel ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.35,
              borderRadius: "999px",
              bgcolor: metric.vsGoalPositive
                ? "color-mix(in srgb, var(--portal-accent) 22%, transparent)"
                : "rgba(255,255,255,0.06)",
              color: metric.vsGoalPositive ? "var(--portal-text)" : "var(--portal-text-muted)",
              fontFamily: portalFont,
              fontSize: "0.75rem",
              fontWeight: 700,
            }}
          >
            {metric.vsGoalPositive ? <NorthEastIcon sx={{ fontSize: 14 }} /> : null}
            {metric.vsGoalLabel}
          </Box>
        ) : null}
        {metric.goalLabel ? (
          <Typography sx={{ fontFamily: portalFont, fontSize: "0.75rem", color: "var(--portal-text-muted)" }}>
            {metric.goalLabel}
          </Typography>
        ) : null}
        {provenance ? (
          <Box
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: "999px",
              border: "1px solid var(--portal-border)",
              fontFamily: portalFont,
              fontSize: "0.6875rem",
              color: "var(--portal-text-muted)",
            }}
          >
            {provenance}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
